import Decimal from "decimal.js";

/**
 * Order Level Distribution Tax Calculator
 * Implements precise tax calculation with order-level distribution for accurate rounding
 */

/**
 * Helper function for precise rounding
 * @param {number} num - Number to round
 * @param {number} precision - Decimal places
 * @returns {string} - Rounded number as string
 */
const toFixed = (num, precision) => {
  return (+(Math.round(+(num + "e" + precision)) + "e" + -precision)).toFixed(
    precision
  );
};

/**
 * Helper function for truncation (no rounding)
 * @param {number} num - Number to truncate
 * @param {number} precision - Decimal places
 * @returns {string} - Truncated number as string
 */
const toFixedNoRounding = (num, precision) => {
  const re = new RegExp("^-?\\d+(?:.\\d{0," + (precision || -1) + "})?");
  return num.toString().match(re)[0];
};

/**
 * Apply tax rounding based on rounding mode
 * This is the centralized rounding function used throughout the application
 * All tax rounding should use this function for consistency
 * @param {number} tax - Tax amount to round
 * @param {string} roundingMode - Rounding mode ('nearest', 'no', 'order_level_distributed')
 * @returns {number} - Rounded tax amount
 */
export const applyTaxRounding = (tax, roundingMode) => {
  if (roundingMode === "nearest") {
    return parseFloat(toFixed(tax, 2));
  } else if (roundingMode === "no") {
    return parseFloat(toFixedNoRounding(tax, 2));
  } else if (roundingMode === "order_level_distributed") {
    // For order level distribution, return raw tax value
    // The actual distribution will be handled by the tax calculator
    return tax;
  }
  return tax;
};

/**
 * Calculate order level distributed tax
 * @param {Array} itemsForDistribution - Array of items with tax information
 * @param {boolean} isTaxInclusive - When true, price includes tax; use inclusive formula for raw tax
 * @returns {Object} - Distribution result with distributed taxes
 */
export const calculateOrderLevelDistributedTax = (
  itemsForDistribution,
  isTaxInclusive = false
) => {
  if (!itemsForDistribution || itemsForDistribution.length === 0) {
    return { distributedTaxes: [], totalTax: 0 };
  }

  // Convert all values to Decimal for precision
  const items = itemsForDistribution.map((item) => ({
    ...item,
    price: new Decimal(item.price),
    tax_rate: new Decimal(item.tax_rate),
    original_tax: new Decimal(item.original_tax || 0),
    quantity: item.quantity || 1
  }));

  // Calculate raw tax for each item (inclusive: extract tax from price; exclusive: price * rate)
  const itemsWithRawTax = items.map((item) => {
    const linePrice = item.price.mul(item.quantity);
    const rawTax = isTaxInclusive
      ? linePrice.minus(linePrice.dividedBy(new Decimal(1).plus(item.tax_rate)))
      : linePrice.mul(item.tax_rate);
    return { ...item, rawTax };
  });

  // Calculate total raw tax
  const totalRawTax = itemsWithRawTax.reduce(
    (sum, item) => sum.add(item.rawTax),
    new Decimal(0)
  );

  // Round total tax to 2 decimal places
  const totalTaxRounded = totalRawTax.toDecimalPlaces(2, Decimal.ROUND_HALF_UP);

  // Calculate each item's proportional share
  const itemsWithProportionalTax = itemsWithRawTax.map((item) => {
    if (totalRawTax.isZero()) {
      return {
        ...item,
        proportionalTax: new Decimal(0),
        roundedTax: new Decimal(0)
      };
    }

    const proportionalTax = item.rawTax.div(totalRawTax).mul(totalTaxRounded);
    const roundedTax = proportionalTax.toDecimalPlaces(
      2,
      Decimal.ROUND_HALF_UP
    );

    return {
      ...item,
      proportionalTax,
      roundedTax
    };
  });

  // Calculate rounding difference
  const distributedTotal = itemsWithProportionalTax.reduce(
    (sum, item) => sum.add(item.roundedTax),
    new Decimal(0)
  );
  const roundingDifference = totalTaxRounded.sub(distributedTotal);

  // Distribute rounding difference to items closest to rounding thresholds
  if (!roundingDifference.isZero()) {
    const itemsWithAdjustments = itemsWithProportionalTax.map((item) => {
      const fractionalPart = item.proportionalTax.mod(1);
      const distanceFromHalf = fractionalPart.sub(0.5).abs();

      return {
        ...item,
        distanceFromHalf: distanceFromHalf.toNumber()
      };
    });

    // Sort by distance from 0.5 (closest to rounding threshold first)
    itemsWithAdjustments.sort(
      (a, b) => a.distanceFromHalf - b.distanceFromHalf
    );

    // Apply rounding difference
    let remainingDifference = roundingDifference;
    for (
      let i = 0;
      i < itemsWithAdjustments.length && !remainingDifference.isZero();
      i++
    ) {
      const adjustment = remainingDifference.greaterThan(0)
        ? new Decimal(0.01)
        : new Decimal(-0.01);

      itemsWithAdjustments[i].roundedTax = itemsWithAdjustments[
        i
      ].roundedTax.add(adjustment);
      remainingDifference = remainingDifference.sub(adjustment);
    }
  }

  // Convert back to regular numbers
  const distributedTaxes = itemsWithProportionalTax.map((item) => ({
    itemId: item.itemId,
    taxRateId: item.taxRateId,
    original_tax: item.original_tax.toNumber(),
    distributed_tax: item.roundedTax.toNumber(),
    tax_rate: item.tax_rate.toNumber(),
    quantity: item.quantity || 1
  }));

  return {
    distributedTaxes,
    totalTax: totalTaxRounded.toNumber()
  };
};

/**
 * Calculate tax per unit for a single item
 * This is the unified formula used by all tax calculators
 * @param {Object} params - Calculation parameters
 * @param {number} params.price - Item base price
 * @param {Array} params.modifiers - Array of modifiers with price property
 * @param {number} params.itemDiscount - Item-level discount
 * @param {number} params.wholeDiscount - Whole order discount applied to item
 * @param {number} params.taxRateValue - Tax rate as percentage (e.g., 8.5 for 8.5%)
 * @param {boolean} params.isTaxInclusive - Whether tax is included in price
 * @returns {number} - Tax amount per unit (before rounding)
 */
export const calculateTaxPerUnit = ({
  price,
  modifiers = [],
  itemDiscount = 0,
  wholeDiscount = 0,
  taxRateValue,
  isTaxInclusive
}) => {
  // 1. Calculate base price with modifiers
  const modifiersTotal = modifiers.reduce(
    (sum, mod) => sum + (mod.price || 0),
    0
  );
  const totalPriceWithModifiers = price + modifiersTotal;

  // 2. Apply discounts
  const discountedPrice =
    totalPriceWithModifiers - itemDiscount - wholeDiscount;

  // 3. Calculate tax
  const taxRateDecimal = taxRateValue / 100;

  if (isTaxInclusive) {
    // Tax = discountedPrice - (discountedPrice / (1 + taxRate))
    return discountedPrice - discountedPrice / (1 + taxRateDecimal);
  } else {
    // Tax = discountedPrice * taxRate
    return discountedPrice * taxRateDecimal;
  }
};

/**
 * Convert a line-level discount into a per-unit discount for tax calculation.
 * Cart promotions store discounts per line, while taxes are calculated per unit.
 */
export const getPerUnitDiscount = (discount, quantity = 1) => {
  const numericDiscount = Number(discount) || 0;
  const numericQuantity = Number(quantity) || 1;
  if (numericQuantity <= 0) return numericDiscount;
  return numericDiscount / numericQuantity;
};

/**
 * Calculate item tax items (individual tax calculations)
 * @param {Object} item - Order item
 * @param {Object} product - Product information
 * @param {Array} taxRates - Available tax rates
 * @param {boolean} isTaxInclusive - Whether tax is included in price
 * @param {string} taxRoundingMode - Tax rounding mode
 * @returns {Array} - Array of tax items
 */
export const calculateItemTaxItems = (
  item,
  product,
  taxRates,
  isTaxInclusive,
  taxRoundingMode
) => {
  const taxItems = [];

  if (!product.tax_rates || product.tax_rates.length === 0) {
    return taxItems;
  }

  // Calculate tax for each tax rate using unified formula
  product.tax_rates.forEach((taxRateId) => {
    const taxRate = taxRates.find((tr) => tr.id === taxRateId);
    if (!taxRate) return;

    const taxValueItem = calculateTaxPerUnit({
      price: item.price,
      modifiers: item.modifiers || [],
      itemDiscount: getPerUnitDiscount(item.discount, item.quantity),
      wholeDiscount: getPerUnitDiscount(item.wholeDiscount, item.quantity),
      taxRateValue: taxRate.rate,
      isTaxInclusive
    });

    // Apply rounding based on mode
    const roundedTaxValue = applyTaxRounding(taxValueItem, taxRoundingMode);
    const taxTotal = roundedTaxValue * item.quantity;

    taxItems.push({
      tax_rate: taxRateId,
      tax_rate_value: taxRate.rate,
      tax_value_item: roundedTaxValue,
      tax_total: taxTotal,
      _distributionId: `${item.local_id}_${taxRateId}`
    });
  });

  return taxItems;
};

/**
 * Calculate order tax with distribution support
 * @param {Object} order - Order object with items
 * @param {Array} taxRates - Available tax rates
 * @param {boolean} isTaxInclusive - Whether tax is included in price
 * @param {string} taxRoundingMode - Tax rounding mode
 * @returns {number} - Total order tax
 */
export const calculateOrderTax = (
  order,
  taxRates,
  isTaxInclusive,
  taxRoundingMode
) => {
  if (taxRoundingMode === "order_level_distributed") {
    // Collect all taxable items for distribution
    const itemsForDistribution = [];

    order.items.forEach((item) => {
      if (item.void_status) return; // Skip voided items

      const modifiersTotal = item.modifiers
        ? item.modifiers.reduce((sum, mod) => sum + mod.price, 0)
        : 0;
      const totalPriceWithModifiers = item.price + modifiersTotal;

      // Apply discounts
      const itemDiscount = getPerUnitDiscount(item.discount, item.quantity);
      const wholeDiscount = getPerUnitDiscount(
        item.wholeDiscount,
        item.quantity
      );
      const discountedPrice =
        totalPriceWithModifiers - itemDiscount - wholeDiscount;

      // Add each tax rate as a separate item for distribution
      if (item.tax_items && item.tax_items.length > 0) {
        item.tax_items.forEach((taxItem) => {
          itemsForDistribution.push({
            itemId: `${item.local_id}_${taxItem.tax_rate}`,
            taxRateId: taxItem.tax_rate,
            price: discountedPrice,
            quantity: item.quantity,
            tax_rate: taxItem.tax_rate_value / 100,
            original_tax: taxItem.tax_value_item
          });
        });
      }
    });

    // Calculate distributed tax (pass isTaxInclusive for inclusive tax formula)
    const distributionResult = calculateOrderLevelDistributedTax(
      itemsForDistribution,
      isTaxInclusive
    );

    // Apply distributed tax back to items
    order.items.forEach((item) => {
      if (item.tax_items && item.tax_items.length > 0) {
        item.tax_items.forEach((taxItem) => {
          const distributedTax = distributionResult.distributedTaxes.find(
            (dt) => dt.itemId === `${item.local_id}_${taxItem.tax_rate}`
          );

          if (distributedTax) {
            const distributedTaxPerUnit =
              distributedTax.distributed_tax /
              (distributedTax.quantity || item.quantity);
            taxItem.tax_value_item = distributedTaxPerUnit;
            taxItem.tax_total = distributedTax.distributed_tax;
          }
        });
      }
    });

    return distributionResult.totalTax;
  } else {
    // Standard tax calculation
    return order.items.reduce((total, item) => {
      if (item.void_status) return total;

      const itemTaxTotal = item.tax_items
        ? item.tax_items.reduce((sum, taxItem) => sum + taxItem.tax_total, 0)
        : 0;

      return total + itemTaxTotal;
    }, 0);
  }
};

/**
 * Apply whole order discount and recalculate taxes
 * @param {Object} order - Order object
 * @param {number} orderDiscount - Total order discount amount
 * @param {Array} taxRates - Available tax rates
 * @param {boolean} isTaxInclusive - Whether tax is included in price
 * @param {string} taxRoundingMode - Tax rounding mode
 */
export const applyWholeOrderDiscount = (
  order,
  orderDiscount,
  taxRates,
  isTaxInclusive,
  taxRoundingMode
) => {
  if (orderDiscount <= 0) return;

  // Calculate order subtotal
  const orderSubtotal = order.items.reduce((total, item) => {
    if (item.void_status) return total;

    const modifiersTotal = item.modifiers
      ? item.modifiers.reduce((sum, mod) => sum + mod.price, 0)
      : 0;
    const itemSubtotal = (item.price + modifiersTotal) * item.quantity;

    return total + itemSubtotal;
  }, 0);

  if (orderSubtotal <= 0) return;

  // Calculate discount ratio
  const discountRatio = orderDiscount / orderSubtotal;

  // Distribute discount proportionally to items
  order.items.forEach((item) => {
    if (item.void_status) return;

    const modifiersTotal = item.modifiers
      ? item.modifiers.reduce((sum, mod) => sum + mod.price, 0)
      : 0;
    const itemSubtotal = (item.price + modifiersTotal) * item.quantity;
    item.wholeDiscount = itemSubtotal * discountRatio;
  });

  // Recalculate taxes with distributed discount
  order.items.forEach((item) => {
    if (item.void_status) return;

    item.tax_items = calculateItemTaxItems(
      item,
      item.product || {},
      taxRates,
      isTaxInclusive,
      taxRoundingMode
    );
  });
};
